import { useMemo, useState } from "react";

import { useEditor } from "@craftjs/core";
import { MousePointer2, Hand, Redo2, Undo2, Monitor, Smartphone, Paintbrush } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, ToggleOptionGroup, ToggleOptionGroupItem } from "twyne-new.ui";

import { useInteractionMode, useResponsive } from "../context";

export default function FloatingToolbar() {
  return <div className="absolute bottom-4 inset-x-0 flex justify-center">
    <div className="shadow-[0_0_10px_rgba(0,0,0,0.13)] rounded-md twy-bg-base-accent w-fit flex hover:opacity-100 opacity-50 transition-all duration-300">
      <InteractionMode />
      <History />
      <Responsive />
    </div>
  </div>
}

function InteractionMode() {
  const { value: interactionMode, setValue: setInteractionMode } = useInteractionMode()

  const { selected } = useEditor(state => ({ selected: state.events.selected }))
  const disabled = useMemo(() => {
    const nodes = [...selected]
    return nodes.length !== 1 || nodes.some(node => node === "ROOT")
  }, [selected])

  return <ToggleOptionGroup
    size="md"
    type="single"
    value={interactionMode}
    onValueChange={setInteractionMode}
  >
    <ToggleOptionGroupItem
      aria-label="Toggle select"
      value="select"
    >
      <MousePointer2 size={16} />
    </ToggleOptionGroupItem>
    <ToggleOptionGroupItem
      aria-label="Toggle move"
      value="move"
    >
      <Hand size={16} />
    </ToggleOptionGroupItem>
    <ToggleOptionGroupItem
      aria-label="Toggle format painter"
      value="format_painter:copy"
      disabled={disabled}
    >
      <Paintbrush size={16} />
    </ToggleOptionGroupItem>
  </ToggleOptionGroup>
}

function History() {
  const { actions: { selectNode, history: { undo, redo } }, query: { history: { canRedo, canUndo } } } = useEditor()
  const [value, setValue] = useState("")

  const handleValue = value => {
    selectNode()
    if (value === "undo") undo()
    if (value === "redo") redo()
    setValue(value)
  }

  return <ToggleOptionGroup
    size="md"
    type="single"
    value={value}
    onValueChange={() => setValue("")}
  >
    <ToggleOptionGroupItem
      aria-label="Toggle undo"
      value="undo"
      disabled={!canUndo()}
    >
      <Undo2 size={16}
        onMouseDown={() => handleValue("undo")}
      />
    </ToggleOptionGroupItem>
    <ToggleOptionGroupItem
      aria-label="Toggle redo"
      value="redo"
      disabled={!canRedo()}
    >
      <Redo2 size={16}
        onMouseDown={() => handleValue("redo")}
      />
    </ToggleOptionGroupItem>
  </ToggleOptionGroup>
}

function Responsive() {
  const { mobileOptions, device, handleDevice, width } = useResponsive()

  const handleChange = (device, size) => {
    handleDevice(device, size)
  }

  return <ToggleOptionGroup
    size="md"
    type="single"
    value={device}
    onValueChange={handleChange}
  >
    <ToggleOptionGroupItem
      aria-label="Toggle desktop"
      value="desktop"
    >
      <Monitor size={16} />
    </ToggleOptionGroupItem>
    <DropdownMenu onOpenChange={value => value && handleChange("mobile")}>
      <DropdownMenuTrigger>
        <ToggleOptionGroupItem
          aria-label="Toggle mobile"
          value="mobile"
        >
          <div className="flex items-center gap-2">
            <Smartphone size={16} />
            <span className="text-md text-nowrap">
              {mobileOptions.find(option => option.value === width)?.label}
            </span>
          </div>
        </ToggleOptionGroupItem>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-fit rounded-md" align="start">
        <DropdownMenuGroup>
          {mobileOptions.map(({ value, label }) => <DropdownMenuItem
            key={value}
            onClick={() => handleChange("mobile", value)}
          >
            {label}
          </DropdownMenuItem>)}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  </ToggleOptionGroup>
}
